import type { Fund } from './fund';
import type { FundAssets, SerializedFundAssets } from './fund_assets';
import { deserializeFundAssets } from './fund_assets';
import type { SerializedTag, Tag } from './tag';
import { deserializeTag } from './tag';

export function isFundAnalyzable(fund: FundProfile) {
  const assetClass = fund.details?.assetClass;
  return (
    typeof assetClass === 'string' &&
    assetClass !== 'fixed_income' &&
    assetClass !== 'currencies' &&
    assetClass !== 'commodities'
  );
}

export function getFundURL(fundOrProfile: Fund | FundProfile) {
  const fundLink = isFundProfile(fundOrProfile) ? fundOrProfile.details?.url : fundOrProfile.infoLink;
  return fundLink ?? `https://www.google.com/search?q=${encodeURIComponent(fundOrProfile.name)}`;
}

export function isFundProfile(fundOrProfile: Fund | FundProfile): fundOrProfile is FundProfile {
  return 'id' in fundOrProfile;
}

export interface FundProfile {
  id: string;
  name: string;
  provider: string;
  domiciles: string[];
  tags?: Tag[];
  details?: FundDetails;
  holdings?: FundAssets;
}

export interface FundDetails {
  tickers?: string[];
  assetClass: string;
  isSynthetic: boolean;
  url: string;
  ter: number;
  aum: number;
  currency?: string;
  price?: number;
  timestamp?: string;
}

export interface SerializedFundProfile {
  i: string;
  n: string;
  p: string;
  d: string[];
  tg?: SerializedTag[];
  fd?: SerializedFundDetails;
  h?: SerializedFundAssets;
}

export interface SerializedFundDetails {
  tk?: string[];
  ac: string;
  r: string;
  u: string;
  t: number;
  a: number;
  cr?: string;
  p?: number;
  ts: number;
}

export function deserializeFundProfile(serializedFundProfile: SerializedFundProfile): FundProfile {
  const profile: FundProfile = {
    id: serializedFundProfile.i,
    name: serializedFundProfile.n,
    provider: serializedFundProfile.p,
    domiciles: serializedFundProfile.d,
  };

  if (serializedFundProfile.tg) {
    profile.tags = serializedFundProfile.tg.map((serializedTag) => deserializeTag(serializedTag));
  }

  if (serializedFundProfile.fd) {
    profile.details = deserializeFundDetails(serializedFundProfile.fd);
  }

  if (serializedFundProfile.h) {
    profile.holdings = deserializeFundAssets(serializedFundProfile.h);
  }

  return profile;
}

function deserializeFundDetails(serializedFundDetails: SerializedFundDetails): FundDetails {
  return {
    tickers: serializedFundDetails.tk,
    assetClass: serializedFundDetails.ac,
    isSynthetic: serializedFundDetails.r === 's',
    url: serializedFundDetails.u,
    ter: serializedFundDetails.t,
    aum: serializedFundDetails.a,
    currency: serializedFundDetails.cr,
    price: serializedFundDetails.p,
    timestamp: new Date(serializedFundDetails.ts).toISOString(),
  };
}
